import { useQuery } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import type { ExpandedState } from '@tanstack/react-table';
import { Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import { RootState, useAppSelector } from 'src/services/redux/app/Store';
import DataTableLoading from '../atoms/DataTableLoading';
import DataTableNotFoundSection from '../atoms/DataTableNotFoundSection';
import useDataTableUtilityFunctions from '../hooks/use-data-table-utility-functions';
import DataTableFooter from '../molecules/DataTableFooter';
import { DATA_TABLE_DEFAULT_DATA, DATA_TABLE_INITIAL_PAGINATION, DATA_TABLE_DEFAULT_RESPONSE_ADAPTER } from '../shared/constants';
import type { TDataTableQueryKey, IDataTablePagination, IDataTableRef, IDataTableQueryFnParams, IDataTablePaginationMeta, IDataTableProps } from '../shared/types';
import { DataTableNotFoundMode, DataTableStatus } from '../shared/types';

/**
 * DataTableInner - DataTable Component'inin Ana Implementasyonu
 *
 * Bu component, DataTable'ın tüm iş mantığını yönetir:
 * - React Query ile veri çekme ve önbellekleme
 * - Pagination ve filtreleme state yönetimi
 * - TanStack Table entegrasyonu
 * - Loading, error, empty state yönetimi
 * - Utility fonksiyonlar (satır silme, geçici veri saklama)
 * - Ref üzerinden dışarıya API sunma
 *
 * @template TData - Tabloda gösterilecek veri tipi
 * @template TQueryFn - Veri çekme fonksiyonunun tipi
 *
 * @param props - DataTable props'ları (IDataTableProps)
 * @param ref - Component referansı (IDataTableRef)
 *
 * @returns JSX.Element - Tablo UI'ı
 */
export default function DataTableInner<TData extends object, TQueryFn extends (params: IDataTableQueryFnParams) => Promise<any>>(
  props: IDataTableProps<TData, TQueryFn>,
  ref: React.Ref<IDataTableRef>,
) {
  const {
    columns,
    queryKey: queryKeyInitial,
    queryFn,
    responseAdapter,
    initial,
    classNames,
    paginationProps,
    pageSizeProps,
    notFoundOptions,
    filterSerializer,
    rowIdSelector,
    syncWithUrl = false,
    enableRowSelection = false,
    onRowSelectionChange,
    getRowCanExpand,
    renderSubRow,
    expanderColumnWidth,
  } = props;

  const { filters: initialFilters = {} } = initial || {};
  const initialPagination = useMemo<IDataTablePagination>(() => {
    return {
      ...DATA_TABLE_INITIAL_PAGINATION,
      ...initial?.pagination,
    };
  }, [initial?.pagination]);

  const {
    wrapper: wrapperClassName,

    table: tableClassName,

    tableHeader: tableHeaderClassName,
    tableHeaderRow: tableHeaderRowClassName,

    tableBody: tableBodyClassName,
    tableBodyRow: tableBodyRowClassName,

    footer: footerClassName,

    paginationWrapper: paginationWrapperClassName,

    pageSizeWrapper: pageSizeWrapperClassName,
  } = classNames || {};

  const vendorId = useAppSelector((state: RootState) => state.auth.user?.vendor.id);
  const history = useHistory();
  const location = useLocation();

  /**
   * syncWithUrl: URL'den pagination değerlerini oku (ilk render).
   * URL'de page/limit yoksa initialPagination kullanılır.
   */
  const getInitialPaginationFromUrl = (): IDataTablePagination => {
    if (!syncWithUrl) return initialPagination;

    const searchParams = new URLSearchParams(location.search);
    const rawPage = searchParams.get('page');
    const rawLimit = searchParams.get('limit');

    const parsedPage = rawPage ? parseInt(rawPage, 10) : null;
    const parsedLimit = rawLimit ? parseInt(rawLimit, 10) : null;

    return {
      pageIndex: parsedPage && !isNaN(parsedPage) && parsedPage >= 1 ? parsedPage - 1 : initialPagination.pageIndex,
      pageSize: parsedLimit && !isNaN(parsedLimit) && parsedLimit >= 1 ? parsedLimit : initialPagination.pageSize,
    };
  };

  const [paginationState, setPaginationState] = useState<IDataTablePagination>(getInitialPaginationFromUrl);
  const [filtersState, setFiltersState] = useState<Record<string, any>>(initialFilters);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [tableTempData, setTableTempData] = useState<Record<string | number, any>>({});
  const [rowTempData, setRowTempData] = useState<Record<string | number, any>>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const params = useMemo<IDataTableQueryFnParams>(() => {
    const _pagination = Object.assign({}, paginationState);

    _pagination.pageIndex = Number(_pagination?.pageIndex);
    if (isNaN(_pagination.pageIndex)) _pagination.pageIndex = initialPagination.pageIndex;
    _pagination.pageIndex = Math.max(1, _pagination.pageIndex + 1);

    return {
      vendorId: vendorId || null,
      filters: pickBy(filterSerializer ? filterSerializer(filtersState) : filtersState, identity),
      pagination: _pagination,
    };
  }, [vendorId, filtersState, paginationState, initialPagination, filterSerializer]);

  const queryKey = useMemo<TDataTableQueryKey>(() => {
    const paginationSearchParams = new URLSearchParams({
      page: params.pagination.pageIndex?.toString() || '',
      limit: params.pagination.pageSize?.toString() || '',
    });

    const filterSearchParams = new URLSearchParams(params.filters);

    const queryKeyResult = typeof queryKeyInitial === 'function' ? queryKeyInitial(params) : queryKeyInitial;

    return [...queryKeyResult, params.vendorId, filterSearchParams.toString(), paginationSearchParams.toString()];
  }, [queryKeyInitial, params]);

  const getDataQuery = useQuery({
    queryKey: queryKey,
    queryFn: () => queryFn(params),

    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,

    // 5m
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 5,

    onSuccess: () => {},
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || error?.message || 'Veriler çekilirken bir sorun oluştu', { autoClose: 1500 });
    },

    enabled: queryKey?.length > 0,
  });

  const defaultData = useMemo(() => {
    return DATA_TABLE_DEFAULT_DATA;
  }, []);

  const data = useMemo(() => {
    if (!getDataQuery.isSuccess) return defaultData;
    const adapter = responseAdapter || DATA_TABLE_DEFAULT_RESPONSE_ADAPTER;
    return adapter(getDataQuery.data);
  }, [getDataQuery, responseAdapter, defaultData]);

  const paginationMeta = useMemo<IDataTablePaginationMeta>(() => {
    return {
      itemCount: data?.itemCount || defaultData?.itemCount || 0,
      pageSize: paginationState.pageSize || 0,
      pageCount: data?.pageCount || defaultData?.pageCount || 0,
    };
  }, [data, defaultData, paginationState]);

  const tableStatus = useMemo<DataTableStatus>(() => {
    const dataLength = data?.items?.length || 0;

    if (getDataQuery.isLoading) return DataTableStatus.loading;
    if (getDataQuery.isFetching) return DataTableStatus.fetching;
    if (getDataQuery.isError) return DataTableStatus.error;

    if (getDataQuery.isSuccess) {
      if (dataLength === 0) return DataTableStatus.empty;
      return DataTableStatus.success;
    }

    return DataTableStatus.empty;
  }, [getDataQuery, data]);

  /**
   * Tablo utility fonksiyonları.
   * Bu fonksiyonlar table.options.meta üzerinden erişilebilir.
   * Özellikle cell renderer'lar içinde kullanışlıdır.
   */
  const tableUtilityFunctions = useDataTableUtilityFunctions({
    queryKey,
    tableTempData,
    setTableTempData,
    rowTempData,
    setRowTempData,
  });

  const table = useReactTable({
    data: data?.items || defaultData?.items,
    columns,

    rowCount: data?.itemCount || defaultData?.itemCount,

    state: {
      pagination: paginationState,
      ...(enableRowSelection ? { rowSelection } : {}),
      ...(getRowCanExpand ? { expanded } : {}),
    },

    getRowId: (row) => {
      const customRowId = rowIdSelector ? rowIdSelector(row) : undefined;
      if (customRowId) return customRowId;

      if (row?._rowId) return row._rowId;
      if (row?.original?.id) return row.original.id;
      if (row?.id) return row.id;
      return undefined;
    },

    onPaginationChange: (pagination) => {
      setPaginationState(pagination);
    },

    onRowSelectionChange: enableRowSelection ? (updater: any) => setRowSelection(updater) : undefined,
    enableRowSelection: !!enableRowSelection,

    ...(getRowCanExpand
      ? {
          getRowCanExpand,
          onExpandedChange: setExpanded,
          getExpandedRowModel: getExpandedRowModel(),
        }
      : {}),

    getCoreRowModel: getCoreRowModel(),

    manualPagination: true,
    manualSorting: true,

    debugAll: process.env.NODE_ENV === 'development',

    defaultColumn: {
      minSize: 50,
      maxSize: 500,
    },

    meta: {
      ...tableUtilityFunctions,
    },
  });

  const notFoundSection = useMemo(() => {
    return <DataTableNotFoundSection {...notFoundOptions} />;
  }, [notFoundOptions]);

  /**
   * Filtre yönetimi fonksiyonları.
   * Bu fonksiyonlar ref üzerinden dışarıya açılır ve programatik filtre kontrolü sağlar.
   */
  /** Filtreleri günceller. State veya updater fonksiyon kabul eder. */
  const setFilters = useCallback<IDataTableRef['setFilters']>((stateOrAction) => {
    setFiltersState(stateOrAction);
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);
  /** Tüm filtreleri sıfırlar ve boş obje yapar. */
  const resetFilters = useCallback<IDataTableRef['resetFilters']>(() => {
    setFilters({});
  }, [setFilters]);

  /**
   * Pagination yönetimi fonksiyonları.
   * Bu fonksiyonlar ref üzerinden dışarıya açılır ve programatik sayfa kontrolü sağlar.
   */
  /** Pagination durumunu günceller. State veya updater fonksiyon kabul eder. */
  const setPagination = useCallback<IDataTableRef['setPagination']>((stateOrAction) => {
    setPaginationState(stateOrAction);
  }, []);
  /** Pagination'ı başlangıç değerine döndürür (tablo varsayılanı + `initial.pagination`). */
  const resetPagination = useCallback<IDataTableRef['resetPagination']>(() => {
    setPagination(initialPagination);
  }, [setPagination, initialPagination]);
  /** Bir sonraki sayfaya geçer. Son sayfadaysa bir şey yapmaz. */
  const nextPage = useCallback<IDataTableRef['nextPage']>(() => {
    setPagination((prev) => ({ ...prev, pageIndex: (prev.pageIndex || 0) + 1 }));
  }, [setPagination]);
  /** Bir önceki sayfaya geçer. İlk sayfadaysa (pageIndex: 0) bir şey yapmaz. */
  const previousPage = useCallback<IDataTableRef['previousPage']>(() => {
    setPagination((prev) => ({ ...prev, pageIndex: Math.max(0, (prev.pageIndex || 0) - 1) }));
  }, [setPagination]);
  /** Sayfa başına gösterilecek kayıt sayısını ayarlar. Minimum 1 olmalıdır. */
  const setLimit = useCallback<IDataTableRef['setLimit']>(
    (limit) => {
      setPagination((prev) => ({ ...prev, pageSize: Math.max(1, limit) }));
    },
    [setPagination],
  );

  /**
   * syncWithUrl: Pagination state değiştiğinde URL'i güncelle.
   * İlk render'da URL'de params yoksa replace ile default değerleri yazar.
   * Sonraki değişikliklerde push ile yeni history entry oluşturur.
   */
  const isInitialUrlSyncRef = useRef(true);

  useEffect(() => {
    if (!syncWithUrl) return;

    const searchParams = new URLSearchParams(location.search);
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const newPage = String(paginationState.pageIndex + 1);
    const newLimit = String(paginationState.pageSize);

    // URL zaten güncel ise tekrar yazmaya gerek yok
    if (urlPage === newPage && urlLimit === newLimit) {
      isInitialUrlSyncRef.current = false;
      return;
    }

    searchParams.set('page', newPage);
    searchParams.set('limit', newLimit);

    const newEntry = { pathname: location.pathname, search: searchParams.toString() };

    if (isInitialUrlSyncRef.current) {
      // İlk render: replace (history kirlenmez, page=0 bug fix)
      history.replace(newEntry);
      isInitialUrlSyncRef.current = false;
    } else {
      history.push(newEntry);
    }
  }, [syncWithUrl, paginationState.pageIndex, paginationState.pageSize]);

  /**
   * syncWithUrl: Browser back/forward (POP) olduğunda pagination state'ini URL'den güncelle.
   * Bu, ReactPaginate'in forcePage prop'unun da güncellenmesini sağlar.
   */
  useEffect(() => {
    if (!syncWithUrl) return;
    const unlisten = history.listen((newLocation, action) => {
      if (action === 'POP') {
        const searchParams = new URLSearchParams(newLocation.search);
        const popPage = parseInt(searchParams.get('page') || String(initialPagination.pageIndex + 1), 10);
        const popLimit = parseInt(searchParams.get('limit') || String(initialPagination.pageSize), 10);
        setPaginationState({
          pageIndex: Math.max(0, popPage - 1),
          pageSize: Math.max(1, popLimit),
        });
      }
    });
    return unlisten;
  }, [syncWithUrl, history, initialPagination]);

  const getSelectedRows = useCallback<IDataTableRef['getSelectedRows']>(() => {
    return table.getSelectedRowModel().rows.map((r) => r.original as any) as any[];
  }, [table]);
  const clearSelection = useCallback<IDataTableRef['clearSelection']>(() => {
    setRowSelection({});
  }, []);

  useEffect(() => {
    onRowSelectionChange?.(rowSelection);
  }, [rowSelection, onRowSelectionChange]);

  /** Sayfa veya sayfa boyutu değiştiğinde seçimleri temizle. */
  useEffect(() => {
    if (enableRowSelection) setRowSelection({});
  }, [enableRowSelection, paginationState.pageIndex, paginationState.pageSize]);

  useImperativeHandle(
    ref,
    (): IDataTableRef => {
      return {
        queryKey: queryKey,

        // Filters
        filters: filtersState,
        setFilters: setFilters,
        resetFilters: resetFilters,

        // Pagination
        pagination: paginationState,
        setPagination: setPagination,
        resetPagination: resetPagination,
        nextPage: nextPage,
        previousPage: previousPage,

        // Limit
        setLimit: setLimit,

        // Row selection (enableRowSelection: true ise)
        rowSelection,
        getSelectedRows,
        setRowSelection,
        clearSelection,

        // useDataTableUtilityFunctions'taki aynı fonksiyon; üst bileşen ref ile invalidate edebilsin diye açılıyor (tek implementasyon).
        invalidateDataTableQuery: tableUtilityFunctions.invalidateDataTableQuery,
      };
    },
    [
      queryKey,
      filtersState,
      setFilters,
      resetFilters,
      paginationState,
      setPagination,
      resetPagination,
      nextPage,
      previousPage,
      setLimit,
      rowSelection,
      getSelectedRows,
      clearSelection,
      tableUtilityFunctions,
    ],
  );

  if (tableStatus === DataTableStatus.empty && notFoundOptions?.mode === DataTableNotFoundMode.showInsteadOfTable) {
    return notFoundSection;
  }

  return (
    <div className={clsx('table-responsive mt-5 position-relative', wrapperClassName)}>
      {/** Loading OR Fetching (Revalidating etc.) */}
      {[DataTableStatus.loading, DataTableStatus.fetching].includes(tableStatus) ? <DataTableLoading /> : null}

      {/* begin::Table */}
      <table className={clsx('table table-row-gray-300 align-middle gs-0 gy-4', tableClassName)}>
        {/* begin::Table head */}
        <thead className={clsx('text-muted bg-light', tableHeaderClassName)}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={clsx('fw-bolder', tableHeaderRowClassName)}>
              {headerGroup.headers.map((header) => {
                return (
                  <th key={header.id} colSpan={header.colSpan} className={header.column.columnDef?.meta?.columnClassName} style={header.column.columnDef?.meta?.columnStyle}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {/* end::Table head */}

        {/* begin::Table body */}
        <tbody className={clsx(tableBodyClassName)}>
          {/** Success OR Fetching (Revalidating etc.) */}
          {[DataTableStatus.success, DataTableStatus.fetching].includes(tableStatus)
            ? table.getRowModel().rows.map((row, rowIdx) => {
                const isExpanded = !!renderSubRow && row.getIsExpanded();

                return (
                  <Fragment key={row.id}>
                    <tr
                      className={clsx(tableBodyRowClassName, {
                        'bg-light': rowIdx % 2 === 1,
                      })}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <td key={cell.id} className={cell.column.columnDef?.meta?.cellClassName} style={cell.column.columnDef?.meta?.cellStyle}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>

                    {isExpanded ? (
                      <tr
                        className={clsx('data-table-sub-row', {
                          'bg-light': rowIdx % 2 === 1,
                        })}
                      >
                        {expanderColumnWidth && <td style={{ width: expanderColumnWidth }} />}
                        <td colSpan={expanderColumnWidth ? row.getVisibleCells().length - 1 : row.getVisibleCells().length} className="p-0">
                          {renderSubRow?.(row)}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            : null}

          {/** Loading */}
          {[DataTableStatus.loading].includes(tableStatus) ? (
            <tr>
              <td colSpan={table.getAllColumns().length} className="text-center">
                <span className="fs-7 text-muted text-center d-block w-100 fw-bold">Yükleniyor...</span>
              </td>
            </tr>
          ) : null}

          {/** Empty OR Error */}
          {[DataTableStatus.empty, DataTableStatus.error].includes(tableStatus) ? (
            <tr>
              <td colSpan={table.getAllColumns().length} className="w-100">
                {notFoundSection}
              </td>
            </tr>
          ) : null}
        </tbody>
        {/* end::Table body */}
      </table>
      {/* end::Table */}

      {/* begin::Footer */}
      <DataTableFooter
        setPagination={setPagination}
        paginationState={paginationState}
        paginationMeta={paginationMeta}
        paginationProps={paginationProps}
        pageSizeProps={pageSizeProps}
        classNames={{
          footer: footerClassName,
          paginationWrapper: paginationWrapperClassName,
          pageSizeWrapper: pageSizeWrapperClassName,
        }}
      />
      {/* end::Footer */}
    </div>
  );
}
